import React, { useEffect, useState, useCallback, useMemo } from 'react';
import Navbar from '../components/navbar';
import createApiInstance from '../utilities/apiAuth';
import { useNavigate } from 'react-router-dom';
import MultipleFranchiseeDropdown from '../components/MultipleFranchiseeDropdown';
import RoleDropdown from '../components/roleDropdown';
import Swal from 'sweetalert2';

export default function UserList() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [showAdmins, setShowAdmins] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Memoize fetchUsers to avoid dependency issues
  const fetchUsers = useCallback(async () => {
    const api = createApiInstance(navigate);
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/users/list');
      setUsers(res.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      setError('Failed to load users. Please try again.');
      Swal.fire({
        title: 'Error',
        text: 'Failed to load users. Please refresh the page.',
        icon: 'error',
        confirmButtonText: 'Retry',
        confirmButtonColor: '#3B82F6',
      }).then((result) => {
        if (result.isConfirmed) {
          fetchUsers();
        }
      });
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleRoleUpdate = (userId, newRole) => {
    setUsers((prevUsers) =>
      prevUsers.map((user) => (user.id === userId ? { ...user, role: newRole } : user))
    );
  };

  const handleUserUpdate = (updatedUser) => {
    setUsers((prevUsers) =>
      prevUsers.map((user) => (user.id === updatedUser.id ? updatedUser : user))
    );
  };

  const handleDropdownClick = async () => {
    if (!showAdmins) {
      await Swal.fire({
        title: 'Enable "Show Admins"?',
        text: 'You need to enable "Show Admins" to edit roles.',
        icon: 'info',
        confirmButtonText: 'Enable',
        confirmButtonColor: '#3B82F6',
      });
      setShowAdmins(true);
    }
  };

  const handleShowAdminsChange = () => {
    setShowAdmins((prev) => !prev);
  };

  // Filter and search users with memoization
  const filteredUsers = useMemo(() => {
    let filtered = users.filter((user) => (showAdmins ? true : user.role === 'agent'));
    
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter((user) => {
        const emailMatch = user.email?.toLowerCase().includes(search);
        const roleMatch = user.role?.toLowerCase().includes(search);
        const franchiseeMatch = user.franchiseeAssociations?.some(
          (assoc) => assoc.franchisee?.name?.toLowerCase().includes(search)
        );
        return emailMatch || roleMatch || franchiseeMatch;
      });
    }
    
    return filtered;
  }, [users, showAdmins, searchTerm]);

  // Loading skeleton component
  const LoadingSkeleton = () => (
    <tbody>
      {[...Array(5)].map((_, index) => (
        <tr key={index} className="bg-hs-background border-b border-gray-700 animate-pulse">
          <td className="px-6 py-4">
            <div className="h-4 bg-gray-700 rounded w-48"></div>
          </td>
          <td className="px-6 py-4">
            <div className="h-8 bg-gray-700 rounded w-24"></div>
          </td>
          <td className="px-6 py-4">
            <div className="space-y-2">
              <div className="h-20 bg-gray-700 rounded w-full"></div>
            </div>
          </td>
        </tr>
      ))}
    </tbody>
  );

  return (
    <div className="flex flex-col min-h-screen bg-gray-900">
      <Navbar />
      
      <div className="flex-1 w-full px-4 py-8">
        <div className="max-w-7xl mx-auto">
          {/* Header Section */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-white mb-2">User Management</h1>
            <p className="text-gray-400">Manage users, roles, and franchisee associations</p>
          </div>

          {/* Controls Section */}
          <div className="bg-gray-800 rounded-lg shadow-xl p-6 mb-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              {/* Search Bar */}
              <div className="flex-1 max-w-md">
                <label htmlFor="search" className="sr-only">Search users</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                  <input
                    type="text"
                    id="search"
                    className="block w-full pl-10 pr-3 py-2.5 border border-gray-600 rounded-lg bg-gray-700 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    placeholder="Search by email, role, or franchisee..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  {searchTerm && (
                    <button
                      onClick={() => setSearchTerm('')}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-white"
                    >
                      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>

              {/* Filter Controls */}
              <div className="flex items-center gap-6">
                {/* User Count */}
                <div className="text-sm">
                  <span className="text-gray-400">Showing </span>
                  <span className="text-white font-semibold">{filteredUsers.length}</span>
                  <span className="text-gray-400"> of </span>
                  <span className="text-white font-semibold">{users.length}</span>
                  <span className="text-gray-400"> users</span>
                </div>

                {/* Show Admins Toggle */}
                <div className="flex items-center gap-3 bg-gray-700 px-4 py-2 rounded-lg">
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      id="showAdmins"
                      className="sr-only peer"
                      checked={showAdmins}
                      onChange={handleShowAdminsChange}
                    />
                    <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                  <label htmlFor="showAdmins" className="text-sm font-medium text-white cursor-pointer select-none">
                    Show Admins
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* Table Section */}
          <div className="bg-gray-800 rounded-lg shadow-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead className="text-xs text-gray-300 uppercase bg-gray-700 border-b border-gray-600">
                  <tr>
                    <th scope="col" className="px-6 py-4 font-semibold">
                      <div className="flex items-center gap-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                        Email
                      </div>
                    </th>
                    <th scope="col" className="px-6 py-4 font-semibold">
                      <div className="flex items-center gap-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                        </svg>
                        Role
                      </div>
                    </th>
                    <th scope="col" className="px-6 py-4 font-semibold">
                      <div className="flex items-center gap-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                        </svg>
                        Associated Franchisees
                      </div>
                    </th>
                  </tr>
                </thead>

                {loading ? (
                  <LoadingSkeleton />
                ) : error ? (
                  <tbody>
                    <tr>
                      <td colSpan="3" className="px-6 py-12 text-center">
                        <div className="flex flex-col items-center gap-3">
                          <svg className="w-12 h-12 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <p className="text-gray-400">{error}</p>
                          <button
                            onClick={fetchUsers}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                          >
                            Retry
                          </button>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                ) : filteredUsers.length === 0 ? (
                  <tbody>
                    <tr>
                      <td colSpan="3" className="px-6 py-12 text-center">
                        <div className="flex flex-col items-center gap-3">
                          <svg className="w-12 h-12 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                          </svg>
                          <p className="text-gray-400 text-lg">No users found</p>
                          {searchTerm && (
                            <p className="text-gray-500 text-sm">Try adjusting your search terms</p>
                          )}
                        </div>
                      </td>
                    </tr>
                  </tbody>
                ) : (
                  <tbody className="divide-y divide-gray-700">
                    {filteredUsers.map((user) => (
                      <tr 
                        key={user.id} 
                        className="bg-gray-800 hover:bg-gray-750 transition-colors duration-150"
                      >
                        <th scope="row" className="px-6 py-4 font-medium text-white whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                              {user.email?.charAt(0).toUpperCase()}
                            </div>
                            <span>{user.email}</span>
                          </div>
                        </th>
                        <td className="px-6 py-4">
                          <div onClick={!showAdmins ? handleDropdownClick : undefined}>
                            <RoleDropdown
                              value={user.role}
                              userId={user.id}
                              onUpdate={(newRole) => handleRoleUpdate(user.id, newRole)}
                              disabled={!showAdmins}
                            />
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          {user.role === 'agent' ? (
                            <MultipleFranchiseeDropdown 
                              user={user} 
                              onUpdate={handleUserUpdate}
                            />
                          ) : (
                            <span className="text-gray-500 italic text-sm">Admin users don't have franchisee assignments</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                )}
              </table>
            </div>
          </div>

          {/* Footer Stats */}
          {!loading && !error && (
            <div className="mt-6 flex items-center justify-between text-sm text-gray-400">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                  <span>Agents: {users.filter(u => u.role === 'agent').length}</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-purple-500 rounded-full"></div>
                  <span>Admins: {users.filter(u => u.role !== 'agent').length}</span>
                </div>
              </div>
              <div>
                Total Users: {users.length}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
